import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import { OverflowTooltip } from "../../sharedComponent/OverflowTooltip";
import { stringToColor } from "../../sharedComponent/colorUtils";

const dummyPartners = [
  {
    id: 1,
    firstName: "Mark Asdf Bnjkk Bhhh Vfff Mark Asdf Bnjkk Bhhh Vfff",
    lastName: "Taylor",
    phone: "+1 444 333 2222",
    email: "mark.t@partner.com",
    status: "Active",
  },
  {
    id: 2,
    firstName: "Manish",
    lastName: "Gupta",
    phone: "+1 555 666 7777",
    email: "sarah.c@partner.com",
    status: "Active",
  },
  {
    id: 3,
    firstName: "David",
    lastName: "Miller",
    phone: "+1 888 999 0000",
    email: "david.m@partner.com",
    status: "Inactive",
  },
];

interface PartnersListProps {
  partners: any[];
  onSelect: (partner: any) => void;
  isLoading?: boolean;
}

export const PartnersList: React.FC<PartnersListProps> = ({
  partners,
  onSelect,
  isLoading = false,
}) => {
  const [searchText, setSearchText] = useState("");

  const filtered = partners.filter((partner) => {
    const term = searchText.toLowerCase();
    // Support both flat shape (/partner/users) and old nested shape (centralUser)
    const firstName = partner?.name || partner?.centralUser?.name || "";
    const lastName = partner?.lastName || partner?.centralUser?.lastName || "";
    const email = partner?.mailId || partner?.centralUser?.mailId || "";
    const phone = partner?.mobile || partner?.centralUser?.mobile || "";
    return (
      `${firstName} ${lastName}`.toLowerCase().includes(term) ||
      email.toLowerCase().includes(term) ||
      phone.includes(term)
    );
  });

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: "#E2E3E3", fontSize: "16px" }}>
          Partners
        </Typography>
        {!isLoading && (
          <TextField
            size="small"
            placeholder="Search partners..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#A5A8A9", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: "#1F2F34",
                color: "#FFF",
                fontSize: "13px",
                "& fieldset": { borderColor: "#5a5f60" },
                "&:hover fieldset": { borderColor: "#17B2EB" },
                "&.Mui-focused fieldset": { borderColor: "#17B2EB" },
              },
              "& input::placeholder": { color: "#A5A8A9" },
              "& input": { color: "#FFF" },
            }}
          />
        )}
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#17B2EB" }} />
          <Typography sx={{ color: "#A5A8A9", fontSize: "14px" }}>
            Loading partners...
          </Typography>
        </Box>
      ) : partners.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
            gap: 2,
            opacity: 0.9,
          }}
        >
          <Box
            sx={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "rgba(23, 178, 235, 0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(23, 178, 235, 0.1)",
            }}
          >
            <SearchIcon sx={{ color: "#17B2EB", fontSize: 40, opacity: 0.2 }} />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{ color: "#fff", fontSize: "18px", fontWeight: 600, mb: 0.5 }}
            >
              No Partners Available
            </Typography>
            <Typography sx={{ color: "#A5A8A9", fontSize: "14px" }}>
              There is currently no partner information to display.
            </Typography>
          </Box>
        </Box>
      ) : filtered.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
            gap: 2,
            opacity: 0.9,
          }}
        >
          <Box
            sx={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "rgba(23, 178, 235, 0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(23, 178, 235, 0.1)",
            }}
          >
            <SearchIcon sx={{ color: "#17B2EB", fontSize: 40, opacity: 0.2 }} />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{ color: "#fff", fontSize: "18px", fontWeight: 600, mb: 0.5 }}
            >
              No matching results
            </Typography>
            <Typography sx={{ color: "#A5A8A9", fontSize: "14px" }}>
              Try adjusting your search filters.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((partner) => {
            // Support both flat shape (/partner/users) and old nested shape (centralUser)
            const firstName =
              partner?.name || partner?.centralUser?.name || "Unknown";
            const lastName =
              partner?.lastName || partner?.centralUser?.lastName || "";
            const email =
              partner?.mailId || partner?.centralUser?.mailId || "No email";
            const phone =
              partner?.mobile || partner?.centralUser?.mobile || "No phone";
            const status =
              partner?.accountStatus ||
              partner?.centralUser?.accountStatus ||
              "Unknown";
            const userId = partner?.userId || partner?.centralUser?.userId;
            const isActive = status.toUpperCase() === "ACTIVE";
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={userId}>
                <Card
                  onClick={() => onSelect(partner)}
                  sx={{
                    backgroundColor: "#222B2E",
                    border: "1px solid #313838",
                    borderLeft: "4px solid #313838",
                    borderRadius: "10px",
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0, 191, 165, 0.15)",
                      borderColor: "#00BFA5",
                      borderLeftColor: "#00BFA5",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "16px !important",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: stringToColor(`${firstName} ${lastName}`),
                        color: "#FFFFFF",
                        width: 50,
                        height: 50,
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                    >
                      {firstName.charAt(0).toUpperCase()}
                      {lastName ? lastName.charAt(0).toUpperCase() : ""}
                    </Avatar>
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <OverflowTooltip
                        title={`${firstName} ${lastName}`}
                        placement="top"
                        arrow
                      >
                        <Typography
                          sx={{
                            color: "#E2E3E3",
                            fontSize: "16px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {firstName} {lastName}
                        </Typography>
                      </OverflowTooltip>
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#A5A8A9",
                          fontSize: "12px",
                          mt: 0.5,
                        }}
                      >
                        <EmailIcon
                          sx={{
                            fontSize: "14px",
                            color: "#1DE9B6",
                            flexShrink: 0,
                          }}
                        />
                        <OverflowTooltip
                          title={email ?? ""}
                          placement="top"
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {email || "No email"}
                          </Box>
                        </OverflowTooltip>
                      </Typography>
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#A5A8A9",
                          fontSize: "12px",
                          mt: 0.25,
                        }}
                      >
                        <PhoneIcon
                          sx={{
                            fontSize: "14px",
                            color: "#1DE9B6",
                            flexShrink: 0,
                          }}
                        />
                        <OverflowTooltip
                          title={phone ?? ""}
                          placement="top"
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {phone || "No phone"}
                          </Box>
                        </OverflowTooltip>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: isActive ? "#4CAF50" : "#FFC107",
                          fontSize: "12px",
                          border: `1px solid ${isActive ? "#4CAF50" : "#FFC107"}`,
                          borderRadius: "20px",
                          px: 1,
                          py: 0.25,
                          textTransform: "capitalize",
                        }}
                      >
                        {status}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};
